// Cycle-16 format-options toolbar. Renders a horizontal row of
// formatting toggles inside a pill container.
//
// Layout matches Slack's desktop composer toolbar (most-used → less):
//   Bold | Italic | Strike • Link • OL | UL • Quote • InlineCode | CodeBlock
//
// Measurements from Figma `2829:4274` "Frame 1000005021" (inside the
// `Rich Text Editor` section `2829:4379`):
//   - container 336×36, paddings 4/4/4/4, itemSpacing 4, radius 8
//   - each chip Button: 28×28, paddings 4/4, radius 4
//   - separators (`Line 284/285/286`): vertical hairlines 25 tall
//
// Active chip fill mirrors the selection bubble in Figma
// `3279:11567` Frame 1000005020 — indigo `#537EFF` @ ~20%.
//
// The bullet-list / numbered-list / link chips live OUTSIDE the
// `RichTextFormat` enum (which is pinned to the iOS-A/AND-A 7-entry
// contract — see `RichTextEditor.cross-platform.test.tsx`). They
// surface their own callbacks so the cross-platform contract stays
// intact while the web composer gains Slack-parity affordances.

import React, { useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import {
  Bold,
  Braces,
  Check,
  ChevronDown,
  Code,
  CodeXml,
  Italic,
  Link,
  List,
  ListOrdered,
  type LucideIcon,
  Quote,
  Strikethrough,
} from 'lucide-react-native';
import { tokens } from '../../design-system/tokens';
import { CODE_LANGUAGES, languageLabel } from '@ping/enriched-text-input';
import { type RichTextFormat } from './RichTextFormat';
import {
  Popover,
  PopoverAnchor,
  type PopoverAnchorHandle,
} from './Popover';

export interface FormatOptionsProps {
  activeFormats: ReadonlySet<RichTextFormat>;
  onToggle: (format: RichTextFormat) => void;
  // ----- Extended (non-cross-platform-contract) affordances -----
  /** Highlight the bullet-list chip. Defaults to `false`. */
  readonly isBulletListActive?: boolean;
  /** Highlight the numbered-list chip. Defaults to `false`. */
  readonly isNumberedListActive?: boolean;
  /** Highlight the link chip (caret/selection inside a link). */
  readonly isLinkActive?: boolean;
  readonly onToggleBulletList?: () => void;
  readonly onToggleNumberedList?: () => void;
  readonly onInsertLink?: () => void;
  /** True when the caret sits inside a code block — reveals the language selector. */
  readonly codeBlockActive?: boolean;
  /** Current code-block language (explicit or auto-detected), or null. */
  readonly codeLanguage?: string | null;
  /** Pick a code-block language. */
  readonly onSelectCodeLanguage?: (language: string) => void;
  testID?: string | undefined;
}

type ToolbarSlot =
  | { readonly kind: 'chip'; readonly format: RichTextFormat }
  | { readonly kind: 'separator'; readonly id: string }
  | {
      readonly kind: 'aux';
      readonly id: 'bulletList' | 'numberedList' | 'link';
    };

const TOOLBAR_LAYOUT: ReadonlyArray<ToolbarSlot> = [
  { kind: 'chip', format: 'bold' },
  { kind: 'chip', format: 'italic' },
  { kind: 'chip', format: 'strikethrough' },
  { kind: 'separator', id: 'sep-1' },
  { kind: 'aux', id: 'link' },
  { kind: 'aux', id: 'numberedList' },
  { kind: 'aux', id: 'bulletList' },
  { kind: 'separator', id: 'sep-2' },
  { kind: 'chip', format: 'quote' },
  { kind: 'chip', format: 'inlineCode' },
  { kind: 'separator', id: 'sep-3' },
  { kind: 'chip', format: 'code' },
  { kind: 'chip', format: 'codeBlock' },
];

const FORMAT_ICONS: Readonly<Record<RichTextFormat, LucideIcon>> = {
  bold: Bold,
  italic: Italic,
  strikethrough: Strikethrough,
  quote: Quote,
  inlineCode: Code,
  code: CodeXml,
  codeBlock: Braces,
};

const FORMAT_LABELS: Readonly<Record<RichTextFormat, string>> = {
  bold: 'Bold',
  italic: 'Italic',
  strikethrough: 'Strikethrough',
  quote: 'Quote',
  inlineCode: 'Inline code',
  code: 'Code',
  codeBlock: 'Code block',
};

interface AuxDescriptor {
  readonly icon: LucideIcon;
  readonly label: string;
}

const AUX_DESCRIPTORS: Readonly<Record<'bulletList' | 'numberedList' | 'link', AuxDescriptor>> = {
  link: { icon: Link, label: 'Insert link' },
  numberedList: { icon: ListOrdered, label: 'Numbered list' },
  bulletList: { icon: List, label: 'Bulleted list' },
};

export const FormatOptions = React.memo<FormatOptionsProps>(
  ({
    activeFormats,
    onToggle,
    isBulletListActive = false,
    isNumberedListActive = false,
    isLinkActive = false,
    onToggleBulletList,
    onToggleNumberedList,
    onInsertLink,
    codeBlockActive = false,
    codeLanguage = null,
    onSelectCodeLanguage,
    testID,
  }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    // On react-native-web, Pressable doesn't preventDefault on
    // `mousedown`, so clicking a chip blurs the contenteditable
    // editor and wipes its selection before the press fires —
    // resulting in "the toolbar dismisses instead of clicking".
    // Trapping mousedown here keeps focus + selection on the editor
    // so the format command lands on the same range the user picked.
    const webFocusGuard =
      Platform.OS === 'web'
        ? ({
            onMouseDown: (e: { preventDefault: () => void }) => {
              e.preventDefault();
            },
          } as unknown as Record<string, unknown>)
        : {};

    return (
      <View
        // Figma `2829:4274` "Frame 1000005021": 336×36, padding 4,
        // gap 4, radius 8. Container fill matches the composer
        // surface so the chip floats cleanly above the pill.
        className="
          flex flex-row items-center self-start
          h-9 px-xs py-xs gap-xs rounded-sm
          bg-light-surface-primary dark:bg-[#11131A]
          border border-light-border-secondary dark:border-white/[0.08]
        "
        testID={testID}
        {...webFocusGuard}
      >
        {TOOLBAR_LAYOUT.map((slot) => {
          if (slot.kind === 'separator') {
            return (
              <View
                key={slot.id}
                className="w-px h-[25px] mx-[2px] bg-light-border-secondary dark:bg-white/[0.08]"
              />
            );
          }
          if (slot.kind === 'aux') {
            const descriptor = AUX_DESCRIPTORS[slot.id];
            const Icon = descriptor.icon;
            let onPress: (() => void) | undefined;
            let isActive = false;
            if (slot.id === 'link') {
              onPress = onInsertLink;
              isActive = isLinkActive;
            } else if (slot.id === 'numberedList') {
              onPress = onToggleNumberedList;
              isActive = isNumberedListActive;
            } else {
              onPress = onToggleBulletList;
              isActive = isBulletListActive;
            }
            if (onPress === undefined) return null;
            return (
              <Pressable
                key={slot.id}
                accessibilityRole="button"
                accessibilityLabel={descriptor.label}
                accessibilityState={{ selected: isActive }}
                onPress={onPress}
                className={`
                  w-7 h-7 rounded-xs items-center justify-center
                  ${isActive ? 'bg-light-accent-indigo/20 dark:bg-[#537EFF]/20' : ''}
                `}
                testID={`format-chip-${slot.id}`}
                {...webFocusGuard}
              >
                <Icon
                  size={16}
                  strokeWidth={2}
                  color={isActive ? colors.accent.indigo : colors.text.secondary}
                />
              </Pressable>
            );
          }
          const { format } = slot;
          const isActive = activeFormats.has(format);
          const Icon = FORMAT_ICONS[format];
          return (
            <Pressable
              key={format}
              accessibilityRole="button"
              accessibilityLabel={FORMAT_LABELS[format]}
              accessibilityState={{ selected: isActive }}
              onPress={() => onToggle(format)}
              className={`
                w-7 h-7 rounded-xs items-center justify-center
                ${isActive ? 'bg-light-accent-indigo/20 dark:bg-[#537EFF]/20' : ''}
              `}
              testID={`format-chip-${format}`}
              {...webFocusGuard}
            >
              <Icon
                size={16}
                strokeWidth={2}
                color={isActive ? colors.accent.indigo : colors.text.secondary}
              />
            </Pressable>
          );
        })}
        {/*
          Code-block language selector — revealed only when the caret is
          inside a code block. Anchored popover (Slack/GitHub-style) with
          the detected language pre-highlighted.
        */}
        {codeBlockActive && onSelectCodeLanguage !== undefined ? (
          <>
            <View className="w-px h-[25px] mx-[2px] bg-light-border-secondary dark:bg-white/[0.08]" />
            <CodeLanguageSelector
              language={codeLanguage}
              onSelect={onSelectCodeLanguage}
              webFocusGuard={webFocusGuard}
            />
          </>
        ) : null}
      </View>
    );
  },
);

FormatOptions.displayName = 'FormatOptions';

interface CodeLanguageSelectorProps {
  readonly language: string | null;
  readonly onSelect: (language: string) => void;
  readonly webFocusGuard: Record<string, unknown>;
}

/**
 * Language picker for the active code block. The trigger shows the
 * current (or auto-detected) language; tapping it opens an anchored
 * popover listing the catalog. Mirrors the toolbar's `webFocusGuard`
 * so opening the list doesn't blur the editor + drop the caret.
 */
const CodeLanguageSelector: React.FC<CodeLanguageSelectorProps> = ({
  language,
  onSelect,
  webFocusGuard,
}) => {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<PopoverAnchorHandle | null>(null);
  // `plaintext` / empty → show "Plain Text"; a non-empty id resolves to
  // its human label.
  const activeId = language === null || language.length === 0 ? 'plaintext' : language;
  const label = languageLabel(activeId);

  return (
    <>
      <PopoverAnchor ref={anchorRef}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Code block language"
          accessibilityState={{ expanded: open }}
          onPress={() => setOpen((o) => !o)}
          className="flex flex-row items-center h-7 px-2 gap-1 rounded-xs"
          testID="format-code-language"
          {...webFocusGuard}
        >
          <Text
            numberOfLines={1}
            className="font-sans text-[12px] font-medium text-light-text-secondary dark:text-[#B5BAC3]"
          >
            {label}
          </Text>
          <ChevronDown size={12} strokeWidth={2} color={colors.text.secondary} />
        </Pressable>
      </PopoverAnchor>
      <Popover
        visible={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        placement="top"
        align="end"
        gap={6}
        testID="format-code-language-popover"
      >
        <View
          className="w-[200px] rounded-[12px] p-1 bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.08]"
          {...webFocusGuard}
        >
          <ScrollView style={{ maxHeight: 280 }} keyboardShouldPersistTaps="handled">
            {CODE_LANGUAGES.map((lang) => {
              const selected = lang.id === activeId;
              return (
                <Pressable
                  key={lang.id}
                  accessibilityRole="menuitem"
                  accessibilityState={{ selected }}
                  onPress={() => {
                    onSelect(lang.id);
                    setOpen(false);
                  }}
                  className={`
                    flex flex-row items-center justify-between px-2 py-[6px] rounded-lg
                    hover:bg-light-surface-secondary dark:hover:bg-white/[0.08]
                    ${selected ? 'bg-light-surface-secondary dark:bg-white/[0.06]' : ''}
                  `}
                  testID={`format-code-language-${lang.id}`}
                  {...webFocusGuard}
                >
                  <Text className="font-sans text-[14px] leading-5 text-light-text-primary dark:text-[#F5F6F8]">
                    {lang.label}
                  </Text>
                  {selected ? (
                    <Check size={14} strokeWidth={2} color={colors.accent.indigo} />
                  ) : null}
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </Popover>
    </>
  );
};
